import React from "react";
import { Award, Flame, MapPin, Trophy, Menu, X } from "lucide-react";

const credentials = [
  { icon: Flame, label: "Head Coach · Full Circle MMA" },
  { icon: Trophy, label: "Has led fighters to championships in organisations such as UFN" },
  { icon: Award, label: "Trained across traditional martial arts, boxing, Muay Thai & wrestling since adolescence" },
  { icon: MapPin, label: "Wollongong-based · Unanderra, NSW" },
];

export default function CoachBio({ onToggleJuniorCoach, juniorCoachOpen = false }) {
  return (
    <section
      id="coach"
      data-testid="coach-section"
      className="relative py-28 md:py-36 bg-[#050505]"
    >
      {/* 3-line menu to reveal the Junior Coach (Memoe) — pinned top-right of the coaches section */}
      {typeof onToggleJuniorCoach === "function" && (
        <button
          type="button"
          onClick={onToggleJuniorCoach}
          data-testid="coach-section-menu-toggle"
          aria-label={juniorCoachOpen ? "Hide additional coaches" : "Show additional coaches"}
          aria-expanded={juniorCoachOpen}
          className="absolute top-6 right-6 md:top-8 md:right-8 z-10 flex items-center gap-2 border border-[#d4af37]/40 bg-black/60 backdrop-blur-sm px-3 py-2 md:px-4 md:py-2.5 text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#d4af37] font-bold hover:bg-[#d4af37] hover:text-black transition-colors"
        >
          {juniorCoachOpen ? <X size={14} /> : <Menu size={14} />}
          <span className="hidden sm:inline">
            {juniorCoachOpen ? "Hide Coaches" : "Coaches"}
          </span>
        </button>
      )}
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Portrait block */}
          <div className="lg:col-span-5 relative">
            <div className="group relative aspect-[4/5] w-full max-w-md overflow-hidden">
              <img
                src="https://customer-assets-lxgj4vgw.emergentagent.net/job_fullcircle-mma/artifacts/t1dww928_IMG_20260917_163805.webp"
                alt="Coach Russell Thompson"
                className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                style={{
                  filter: "contrast(1.1) saturate(1.15) brightness(1.02)",
                  imageRendering: "crisp-edges",
                }}
              />
              {/* Subtle gold frame */}
              <div className="pointer-events-none absolute inset-0 ring-1 ring-[#d4af37]/30" />
              {/* Minimal gradient only at the bottom to keep name legible */}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="text-xs uppercase tracking-[0.3em] text-[#d4af37] font-bold">
                  Head Coach
                </div>
                <div className="font-heading text-4xl md:text-5xl uppercase text-white leading-none mt-2">
                  Russell
                  <br />
                  Thompson
                </div>
              </div>
              <div className="absolute -top-3 -right-3 md:-top-4 md:-right-4 bg-[#d4af37] text-black px-3 py-2 md:px-4 md:py-3 font-heading uppercase tracking-tight shadow-[0_10px_30px_-10px_rgba(212,175,55,0.6)]">
                <div className="text-[9px] md:text-xs">Head Coach</div>
                <div className="text-2xl md:text-3xl leading-none">FC MMA</div>
              </div>
            </div>

            {/* Cornering champion — secondary frame (natural aspect, no crop) */}
            <div className="group mt-4 relative w-full max-w-md overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_fullcircle-mma/artifacts/2tja3pp5_full%20circle%20pfp.jpg"
                alt="Russell Thompson cornering his fighter to a UFN championship victory"
                className="block w-full h-auto grayscale group-hover:grayscale-0 group-hover:scale-[1.02] transition-all duration-700"
                style={{ filter: "contrast(1.08) saturate(1.1)" }}
                data-testid="coach-corner-photo"
              />
              <div className="pointer-events-none absolute inset-0 ring-1 ring-[#d4af37]/30" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-3">
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/85 font-bold">
                  Cornering a UFN champion
                </div>
                <div className="text-[9px] uppercase tracking-[0.25em] text-[#d4af37] font-bold">
                  Full Circle Corner
                </div>
              </div>
            </div>
          </div>

          {/* Copy */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#d4af37]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#d4af37] font-bold">
                Meet Your Coach
              </span>
            </div>

            <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.9]">
              Your Goal. <br />
              <span className="text-[#d4af37]">His Craft.</span>
            </h2>

            <div className="mt-8 space-y-5 text-white/75 text-base md:text-lg leading-relaxed max-w-2xl">
              <p>
                Russell Thompson grew up with a strong combat-sports
                background — training across traditional martial arts
                alongside boxing, kickboxing, Muay Thai and wrestling long
                before MMA had a name on the Illawarra. He evolved his style
                across every aspect of fighting to forge the battle-tested
                MMA approach that now defines Full Circle MMA's coaching
                method.
              </p>
              <p>
                Two decades on, Russell has paved the way for a generation of
                fighters who've walked through his doors — from amateurs taking
                their first fight, to title contenders. His corner has led
                fighters to championships in organisations such as{" "}
                <span className="text-[#d4af37] font-semibold">UFN</span>, with
                national champions and titleholders developed under his
                guidance.
              </p>
              <p>
                Russell's coaching style is direct, technical and honest. What
                sets him apart is a rare ability to meet every student exactly
                where they are — whether that's discipline, weight loss,
                confidence, self-defence or competition — and draw the best
                version of them out onto the mat, regardless of experience or
                background.
              </p>
            </div>

            <ul className="mt-10 space-y-4">
              {credentials.map((c, i) => (
                <li
                  key={i}
                  data-testid={`coach-credential-${i}`}
                  className="flex items-start gap-4"
                >
                  <span className="mt-0.5 w-10 h-10 border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37]">
                    <c.icon size={16} />
                  </span>
                  <span className="text-white/85 pt-2 text-sm md:text-base">
                    {c.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
